/**
 * Schools API
 * Public endpoints (no auth) for school listing + admin endpoints for CRUD
 */
import apiClient from './client';

export interface SchoolResponse {
  school_id: string;
  name: string;
  slug: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SchoolStatsResponse extends SchoolResponse {
  teacher_count: number;
  student_count: number;
  course_count: number;
  enrollment_count: number;
}

export interface SchoolCreateRequest {
  name: string;
  description?: string;
}

export interface SchoolUpdateRequest {
  name?: string;
  description?: string;
  is_active?: boolean;
}

/**
 * Get all active schools (public, no auth required)
 */
export const getSchools = async (): Promise<SchoolResponse[]> => {
  const response = await apiClient.get<{ schools: SchoolResponse[]; total: number }>('/schools');
  return response.data.schools;
};

/**
 * Get all schools with stats (admin only)
 */
export const getAllSchoolsWithStats = async (): Promise<SchoolStatsResponse[]> => {
  const response = await apiClient.get<SchoolStatsResponse[]>('/schools/all');
  return response.data;
};

/**
 * Get school by ID
 */
export const getSchool = async (schoolId: string): Promise<SchoolResponse> => {
  const response = await apiClient.get<SchoolResponse>(`/schools/${schoolId}`);
  return response.data;
};

/**
 * Create a new school (admin only)
 */
export const createSchool = async (data: SchoolCreateRequest): Promise<SchoolResponse> => {
  const response = await apiClient.post<SchoolResponse>('/schools', data);
  return response.data;
};

/**
 * Update a school (admin only)
 */
export const updateSchool = async (
  schoolId: string,
  data: SchoolUpdateRequest
): Promise<SchoolResponse> => {
  const response = await apiClient.put<SchoolResponse>(`/schools/${schoolId}`, data);
  return response.data;
};

/**
 * Delete a school (admin only)
 */
export const deleteSchool = async (schoolId: string): Promise<void> => {
  await apiClient.delete(`/schools/${schoolId}`);
};
